import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CONFIG } from '@h2h/shared';
import { AppShell, Content } from '../components/ui/AppShell';
import { LivePill } from '../components/ui/bits';
import { ArrowRight, ChevronLeft, Info, LayoutGrid } from '../components/ui/icons';
import { useHaptics } from '../hooks/useHaptics';
import {
  useLeagues,
  useCurrentRound,
  useMyEntry,
  useRoundFixtures,
  useMyGroup
} from '../hooks/useQueries';
import { useAuth } from '../context/AuthContext';
import { identityFor } from '../data/leagues';
import { LiveProvisionalHero } from '../components/rounds/LiveProvisionalHero';
import { PickOverviewRow } from '../components/rounds/PickOverviewRow';
import { DuelOverviewRow } from '../components/rounds/DuelRows';
import { PickLegend } from '../components/rounds/shared';
import { buildCardVMs, fixturesLive, fixturesPlayed, type CardVM, type PickStatus } from '../lib/round';
import { buildDuelVMs, duelsResolved } from '../lib/cyclingRound';
import { LiveDuelSheet } from './match-detail/LiveDuelSheet';
import { LiveFixtureSheet } from './match-detail/LiveFixtureSheet';
import { useNow } from './play/helpers';

/** Tracker order: in-play first, then soonest upcoming, then finished, then void. */
const STATUS_ORDER: Record<PickStatus, number> = { live: 0, upcoming: 1, final: 2, void: 3 };

export default function RoundWaiting() {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const { leagueId } = useParams<{ leagueId: string }>();
  const [params] = useSearchParams();
  const { uid } = useAuth();
  const now = useNow();
  const [liveFixtureId, setLiveFixtureId] = useState<string | null>(null);
  const { data: leagues } = useLeagues();
  const league = leagues?.find((l) => l.leagueId === leagueId);
  const identity = identityFor(leagueId ?? '');
  const name = league?.name ?? identity?.name ?? 'League';
  const isCycling = league?.sport === 'CYCLING';
  const roundId = params.get('round') ?? league?.currentRoundId ?? null;

  const { data: round } = useCurrentRound(league?.leagueId, roundId);
  const { data: entry, isLoading: entryLoading } = useMyEntry(league?.leagueId, roundId, uid);
  const { data: fixtures } = useRoundFixtures(league?.leagueId, roundId, true);
  const { data: group } = useMyGroup(league?.leagueId, roundId, uid, true);

  const fixtureMap = useMemo(() => new Map((fixtures ?? []).map((f) => [f.fixtureId, f])), [fixtures]);

  // Football pick VMs (markets) vs cycling duel VMs (binary A/B), built from the same
  // entry + fixtures via the shared engine so the live numbers match the server.
  const picks = useMemo(() => {
    if (!entry || isCycling) return [];
    return buildCardVMs(entry, fixtureMap, now, round ?? undefined).sort(
      (a: CardVM, b: CardVM) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.kickoffAt - b.kickoffAt
    );
  }, [entry, fixtureMap, now, round, isCycling]);
  const duels = useMemo(
    () => (entry && isCycling ? buildDuelVMs(entry, fixtureMap) : []),
    [entry, fixtureMap, isCycling]
  );

  const myRow = group?.standings.find((r) => r.userId === uid) ?? null;

  // Sport-agnostic hero inputs. Football scores live (provisional points as goals go
  // in); a cycling stage has no in-play state — duels bank straight from the results
  // table once it's clean — so "in play" is 0 and the banked total is the live
  // recomputed roundScore (or the sum of resolved duel points as a fallback).
  const pickCount = isCycling ? duels.length : picks.length;
  const banked = isCycling
    ? myRow?.roundScore ?? duels.reduce((s, d) => s + (d.points ?? 0), 0)
    : myRow?.roundScore ?? picks.reduce((s, p) => s + (p.points ?? 0), 0);
  const inPlay = isCycling ? 0 : picks.reduce((s, p) => s + (p.livePoints ?? 0), 0);
  const played = !entry ? 0 : isCycling ? duelsResolved(fixtureMap) : fixturesPlayed(entry, fixtureMap);
  const liveCount = entry && !isCycling ? fixturesLive(entry, fixtureMap) : 0;
  const isLive = liveCount > 0;
  const open = round?.status === 'OPEN';
  const settling = round?.status === 'SETTLING' || round?.status === 'LOCKING';

  const picksRequired = isCycling
    ? CONFIG.CYCLING.DUELS_PER_STAGE
    : entry?.predictions.length ?? round?.picksRequired ?? 0;

  const eyebrow = settling
    ? `${round?.label ?? name} · settling`
    : `${round?.label ?? name} · ${isLive ? 'live' : open ? 'open' : 'in play'}`;
  const title = isLive ? 'Live now' : open ? (isCycling ? 'Your duels are in' : 'Your cards are in') : isCycling ? 'Your duels are locked' : 'Your cards are locked';
  const blurb = isCycling
    ? open
      ? 'Editable until the stage rolls out. Duels settle from the finish once results land.'
      : settling
        ? 'The stage is under way — your duels settle from the official finishing order.'
        : 'Sit back and watch the duels settle from the stage finish.'
    : isLive
      ? `Following your ${picks.length} card${picks.length === 1 ? '' : 's'} in real time — points update as the goals go in.`
      : open
        ? round?.pickMode === 'FLEXIBLE'
          ? 'Each card stays editable until its own match kicks off. Points update live as matches finish.'
          : 'Editable until the round’s first match kicks off. Points update live as matches finish.'
        : 'Sit back and watch them settle. Points update live as matches finish.';

  const openFixture = (fixtureId: string) => {
    impact('light');
    if (round) setLiveFixtureId(fixtureId);
  };

  return (
    <AppShell surfaceClassName="bg-[linear-gradient(179deg,#1A1A22_0%,#0C0C11_58%,#08080C_100%)] md:bg-none md:bg-canvas">
      <div className="relative min-h-full bg-[linear-gradient(179deg,#1A1A22_0%,#0C0C11_58%,#08080C_100%)] text-white">
        {/* ambient glow — the same green halo the make-picks screen wears */}
        <div
          className="pointer-events-none absolute left-1/2 top-[-40px] h-[300px] w-[340px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(36,197,106,0.16),transparent_66%)]"
          aria-hidden
        />
        <Content width="wide">
          <div className="relative flex items-center justify-between pt-2 md:pt-5">
            <button
              type="button"
              onClick={() => {
                impact('light');
                navigate(leagueId ? `/play/${leagueId}` : '/football');
              }}
              className="tap -ml-1 flex min-w-0 items-center gap-[2px] text-white"
            >
              <ChevronLeft width={22} height={22} strokeWidth={2.4} className="shrink-0" />
              <span className="truncate text-[16px] font-semibold tracking-snug">{name}</span>
            </button>
            {(isLive || round?.status === 'LOCKING' || round?.status === 'SETTLING') && <LivePill tone="on-dark" />}
          </div>

          <div className="relative pt-3">
            <div className="text-[12px] font-bold uppercase tracking-[0.12em] text-white/40">{eyebrow}</div>
            <h1 className="mt-[6px] text-[30px] font-extrabold tracking-[-0.03em] md:text-[36px]">{title}</h1>
            <p className="mt-2 max-w-md text-[14px] font-medium leading-[1.4] text-white/55">{blurb}</p>
          </div>

          {!entry && !entryLoading ? (
            <div className="relative mt-4 flex flex-col items-center rounded-[28px] border border-dashed border-white/15 bg-white/[0.04] px-6 py-14 text-center">
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-white/[0.07]">
                <LayoutGrid width={26} height={26} strokeWidth={1.8} className="text-white/55" />
              </div>
              <div className="mt-4 text-[18px] font-extrabold tracking-tight text-white">
                {isCycling ? 'You haven’t entered this stage' : 'You haven’t entered this round'}
              </div>
              <p className="mt-2 max-w-[300px] text-[14px] font-medium leading-[1.45] text-white/55">
                {isCycling
                  ? 'Call the six duels while the stage is open to join the live board.'
                  : open
                    ? 'Build your cards while the round is open to join the live board.'
                    : 'This round has locked — head back to Compete for the next slate.'}
              </p>
              {open && (
                <button
                  type="button"
                  onClick={() => {
                    impact('medium');
                    navigate(`/play/${leagueId}${roundId ? `?round=${roundId}` : ''}`);
                  }}
                  className="tap mt-5 flex items-center justify-center gap-2 rounded-[16px] bg-[linear-gradient(140deg,#2EDB7B,#0BA078)] px-6 py-[14px] shadow-[0_16px_30px_-16px_rgba(11,160,120,0.7)]"
                >
                  <span className="text-[15px] font-extrabold text-white">
                    {isCycling ? 'Call your duels' : 'Build your cards'}
                  </span>
                  <ArrowRight width={15} height={15} strokeWidth={2.8} className="text-white" />
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="relative pt-4">
                <LiveProvisionalHero
                  banked={banked}
                  inPlay={inPlay}
                  live={isLive}
                  rankInGroup={myRow?.rankInGroup ?? null}
                  divisionSize={group?.memberIds.length ?? null}
                  fixturesPlayed={played}
                  fixturesLive={liveCount}
                  picksRequired={picksRequired}
                  tierIndex={entry?.tier ?? 0}
                />
              </div>

              <div className="relative flex items-center justify-between pt-6">
                <span className="text-[13px] font-bold tracking-snug text-white">
                  {isCycling ? `Your ${pickCount} duels` : isLive ? 'Your cards, live' : `Your ${picks.length} card${picks.length === 1 ? '' : 's'}`}
                </span>
                {!isCycling && <PickLegend variant={isLive ? 'live' : 'settled'} dark />}
              </div>

              <div className="relative flex flex-col gap-[10px] pt-3 md:grid md:grid-cols-2 md:items-start">
                {isCycling
                  ? duels.map((vm) => (
                      <DuelOverviewRow key={vm.duelId} vm={vm} onOpen={() => openFixture(vm.duelId)} />
                    ))
                  : picks.map((pick) => (
                      <PickOverviewRow
                        key={pick.fixtureId}
                        pick={pick}
                        onOpen={() => openFixture(pick.fixtureId)}
                        onEdit={() => {
                          impact('light');
                          navigate(`/play/${leagueId}${roundId ? `?round=${roundId}` : ''}`);
                        }}
                      />
                    ))}
              </div>

              <div className="relative pt-5">
                <div className="flex items-start gap-[9px] rounded-2xl border border-white/[0.08] bg-white/[0.05] p-[13px_14px]">
                  <Info width={16} height={16} strokeWidth={2} className="mt-[1px] shrink-0 text-white/50" />
                  <span className="text-[12px] font-medium leading-[1.45] text-white/55">
                    {isCycling
                      ? 'Cycling results can shift after the line (relegations, time penalties), so a stage settles only once its finishing order is clean and final.'
                      : isLive
                        ? 'Live points are provisional until each match ends. Your final place is set once every fixture finishes.'
                        : "Your place isn't final until every fixture finishes. We'll let you know when the round settles."}
                  </span>
                </div>
              </div>
            </>
          )}
        </Content>
      </div>

      {round && entry && (
        isCycling ? (
          <LiveDuelSheet
            openDuelId={liveFixtureId}
            onClose={() => setLiveFixtureId(null)}
            leagueId={leagueId!}
            round={round}
            fixtures={fixtures ?? []}
            entry={entry}
          />
        ) : (
          <LiveFixtureSheet
            openFixtureId={liveFixtureId}
            onClose={() => setLiveFixtureId(null)}
            leagueId={leagueId!}
            round={round}
            fixtures={fixtures ?? []}
            entry={entry}
            nationalTeams={league?.country === 'World'}
          />
        )
      )}
    </AppShell>
  );
}
